import * as Monaco from 'monaco-editor/esm/vs/editor/editor.api';
import * as React from 'react';

import { SRS_EDITOR_OPTIONS } from 'src/containers/MonacoEditor/constants/editor';
import { useEditorIndentation } from 'src/containers/MonacoEditor/hooks/useEditorIndentation';
import {
  registerMonacoLanguages,
  setEditorTheme,
} from 'src/containers/MonacoEditor/utils';
import { useRefCallback } from 'src/utils/hooks/useRefCallback';

type SrsMonacoEditorProps = {
  setValue: (value: string) => void;
  value: string;
};

export const SrsMonacoEditor: React.VFC<SrsMonacoEditorProps> = ({
  setValue,
  value,
}) => {
  const monacoRef = React.useRef<typeof Monaco | null>(null);
  const editorRef = React.useRef<Monaco.editor.IStandaloneCodeEditor | null>(
    null
  );
  const [divRef, setDivRef] = useRefCallback<HTMLDivElement>(null);
  const indentationCtxRef =
    React.useRef<Monaco.editor.IContextKey<boolean> | null>(null);

  const { onDidChangeCursorPosition, onEnter } = useEditorIndentation({
    editorRef,
    indentationCtxRef,
  });

  React.useEffect(() => {
    if (divRef.current && !editorRef.current) {
      const monaco = Monaco;
      monacoRef.current = monaco;

      const editor = Monaco.editor.create(divRef.current, {
        ...SRS_EDITOR_OPTIONS,
        value,
      });
      editorRef.current = editor;
      indentationCtxRef.current = editor.createContextKey(
        'indentationCtx',
        false
      );

      setEditorTheme(editor);
      editor.addCommand(Monaco.KeyCode.Enter, onEnter, 'indentationCtx');
      editor.onDidChangeCursorPosition(onDidChangeCursorPosition);

      // initiate with the starting cursor position
      onDidChangeCursorPosition();

      editor.onDidChangeModelContent(() => {
        const newValue = editor.getValue();
        setValue(newValue);
      });
    }
  }, [divRef, onDidChangeCursorPosition, onEnter, setValue, value]);

  React.useEffect(() => {
    registerMonacoLanguages();

    return () => {
      editorRef.current?.dispose();
    };
  }, []);

  return <div ref={setDivRef} className="sd-monaco-editor-container" />;
};
